import React, { useState, useEffect, useRef, useId } from 'react';
import axios from 'axios';
import { ExternalLink, X, Volume2, VolumeX } from 'lucide-react';
import AudioAdPlayer from './AudioAdPlayer';
import { Capacitor } from '@capacitor/core';
import { getMediaUrl } from '../utils/mediaUrl';
import adMediaManager from '../utils/adMediaManager';
import { useLanguage } from '../context/LanguageContext';

const AdDisplay = ({ zone = 'feed', refreshKey = 0 }) => {
  const [ad, setAd] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [muted, setMuted] = useState(true);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAutoUnmuted, setHasAutoUnmuted] = useState(false);
  const { language } = useLanguage();
  
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const uniqueId = useId();
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    loadAd();
    setHasTrackedView(false);
    setHasAutoUnmuted(false);
  }, [zone, refreshKey, language]);

  const loadAd = async () => {
    try {
      const response = await axios.get(`/api/advertisements/random`, { params: { zone, lang: language } });
      if (response.data) {
        setAd(response.data);
        setDismissed(false);
        setHasTrackedView(false);
        setMuted(true); // Start muted
        setHasAutoUnmuted(false);
      }
    } catch (error) {
      console.error('Error loading ad:', error);
    }
  };

  // Register video with global media manager
  useEffect(() => {
    if (ad?.ad_type === 'video') {
      adMediaManager.registerVideo(uniqueId, {
        onForceMute: () => {
          console.log(`🔇 Force muting video: ${uniqueId}`);
          setMuted(true);
          if (videoRef.current) {
            videoRef.current.muted = true;
          }
        }
      });

      return () => {
        adMediaManager.unregisterVideo(uniqueId);
      };
    }
  }, [ad, uniqueId]);

  // Intersection Observer for video visibility-based sound
  useEffect(() => {
    const container = containerRef.current;
    if (!container || ad?.ad_type !== 'video') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const nowVisible = entry.isIntersecting;
          setIsVisible(nowVisible);
          
          // On native iOS: auto-unmute when visible, auto-mute when not visible
          if (isNative) {
            if (nowVisible && !hasAutoUnmuted) {
              // Video became visible - unmute (will mute others)
              adMediaManager.requestVideoUnmute(uniqueId);
              setMuted(false);
              setHasAutoUnmuted(true);
              if (videoRef.current) {
                videoRef.current.muted = false;
              }
              console.log(`🔊 Video ad ${uniqueId} visible - unmuting`);
            } else if (!nowVisible && !muted) {
              // Video left viewport - mute it
              setMuted(true);
              if (videoRef.current) {
                videoRef.current.muted = true;
              }
              adMediaManager.notifyVideoMuted(uniqueId);
              console.log(`🔇 Video ad ${uniqueId} left viewport - muting`);
            }
          }
        });
      },
      {
        threshold: 0.6, // 60% visible
        rootMargin: '0px'
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [ad, isNative, muted, hasAutoUnmuted, uniqueId]);

  // Track view when ad is displayed
  useEffect(() => {
    if (ad && !hasTrackedView && !dismissed) {
      trackView();
      setHasTrackedView(true);
    }
  }, [ad, dismissed, hasTrackedView]);

  const trackView = async () => {
    if (!ad) return;
    try {
      await axios.post(`/api/advertisements/${ad.id}/view`);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const handleClick = async () => {
    if (ad) {
      try {
        await axios.post(`/api/advertisements/${ad.id}/click`);
      } catch (error) {
        console.error('Error tracking click:', error);
      }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Mute and notify manager when dismissed
    if (ad?.ad_type === 'video' && !muted) {
      setMuted(true);
      adMediaManager.notifyVideoMuted(uniqueId);
    }
  };

  const toggleMute = () => {
    const newMuted = !muted;
    setMuted(newMuted);
    if (videoRef.current) {
      videoRef.current.muted = newMuted;
    }
    
    if (!newMuted) {
      // Unmuting - request from manager (will mute others)
      adMediaManager.requestVideoUnmute(uniqueId);
    } else {
      // Muting - notify manager
      adMediaManager.notifyVideoMuted(uniqueId);
    }
  };

  // Get proper media URL for native and web
  const getAdMediaUrl = (url) => {
    if (!url) return '';
    return getMediaUrl(url);
  };

  if (!ad || dismissed) return null;

  return (
    <div 
      ref={containerRef}
      className="bg-white/5 backdrop-blur-sm border border-violet-500/30 rounded-2xl overflow-hidden mb-6 relative"
    >
      {/* Sponsored Label */}
      <div className="absolute top-3 left-3 z-10">
        <span className="px-3 py-1 bg-violet-500/80 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
          Sponsorisé
        </span>
      </div>
      
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 z-10 p-2 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-colors"
        title="Masquer"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Ad Content */}
      {ad.ad_type === 'audio' ? (
        <AudioAdPlayer 
          audioUrl={getAdMediaUrl(ad.content_url)} 
          autoPlay={isNative}
          onPlay={() => trackView()}
        />
      ) : (
        <div className="relative">
          {ad.ad_type === 'video' ? (
            <>
              <video
                ref={videoRef}
                src={getAdMediaUrl(ad.content_url)}
                className="w-full aspect-video object-cover"
                autoPlay={true}
                muted={muted}
                loop
                playsInline
                onClick={handleClick}
              />
              {/* Mute/Unmute button for video */}
              <button
                onClick={toggleMute}
                className="absolute bottom-3 right-3 z-10 p-2 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-colors"
                title={muted ? "Activer le son" : "Couper le son"}
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <img
              src={getAdMediaUrl(ad.content_url)}
              alt="Advertisement"
              className="w-full aspect-video object-cover cursor-pointer"
              onClick={handleClick}
            />
          )}
        </div>
      )}

      {/* CTA */}
      <div className="p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10">
        {ad.title && (
          <h3 className="font-semibold text-white mb-2">{ad.title}</h3>
        )}
        {ad.external_link ? (
          <a
            href={ad.external_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center space-x-2 group"
          >
            <span>En savoir plus</span>
            <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        ) : (
          <div className="w-full bg-white/5 text-gray-400 font-medium py-3 px-6 rounded-xl text-center">
            Publicité sponsorisée
          </div>
        )}
      </div>
    </div>
  );
};

export default AdDisplay;
